import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Terminal, Plus, X, Save, Trash2, Send, Eye, Edit3, Copy, Loader2, Mail,
  Users, ArrowRight, ChevronRight, CheckCircle2, XCircle,
} from "lucide-react";

// Phase 25.3c — Outbound Email Campaign Templates.
// Author per-industry templates with merge tags. Send batches of up to 50
// prospects per click. Tracks send count + appends note to each prospect.

const MERGE_TAGS = ["{{first_name}}", "{{name}}", "{{company}}", "{{role}}"];

export default function AdminOutbound() {
  const [templates, setTemplates] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sender, setSender] = useState(null);  // { template, prospect_ids[] }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tplR, pipeR] = await Promise.all([
        api.get("/admin/outbound/templates"),
        api.get("/admin/sales/pipeline?limit=500"),
      ]);
      setTemplates(tplR.data?.items || []);
      setProspects(pipeR.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.subject || !editing.body_text) {
      toast.error("Name, subject, and body text are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: editing.name,
        industry: editing.industry || "",
        subject: editing.subject,
        body_html: editing.body_html || "",
        body_text: editing.body_text,
      };
      if (editing.id) {
        await api.patch(`/admin/outbound/templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/admin/outbound/templates", payload);
        toast.success("Template created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/admin/outbound/templates/${id}`);
      toast.success("Template deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const sendBatch = async (dryRun = false) => {
    if (!sender || sender.prospect_ids.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/outbound/send", {
        template_id: sender.template.id,
        prospect_ids: sender.prospect_ids,
        dry_run: dryRun,
      });
      if (dryRun) {
        toast.success(`Dry run: rendered ${r.data.log.length} previews. No emails sent.`, { duration: 6000 });
        setSender({ ...sender, preview: r.data.log });
      } else {
        toast.success(`Sent ${r.data.total_sent}/${r.data.total_attempted} emails`, { duration: 6000 });
        setSender(null);
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const seedTemplate = (kind) => {
    const seeds = {
      hospitality_intro: {
        name: "Hospitality — Cold intro",
        industry: "Hospitality",
        subject: "Quick one — does {{company}} still reconcile end-of-day by hand?",
        body_text:
`Hi {{first_name}},

Quick one — does {{company}} still reconcile end-of-day by hand? Most coffee shops and restaurants I talk to spend 3-4 hours a night on it.

I'm the founder of DARA — a Bloomberg-style operating terminal for hospitality. We plug into your Square (or Lightspeed, Toast, Stripe Terminal) and replace the spreadsheet chaos with a live DARA Briefing every morning. Operators using it close their day in 4 seconds instead of 4 hours.

Worth a 15-minute look? I can do a live demo with your own data this week.

— [Your name]
P.S. Here's a 60-second screen capture if you'd rather see it first: https://daraos.ai/industries/hospitality`,
      },
      mining_pilot: {
        name: "Mining — Pilot ask (warm)",
        industry: "Mining",
        subject: "{{company}} — DARA pilot programme (30-day, no cost)",
        body_text:
`Hi {{first_name}},

Saw {{company}} on my radar. We're running 30-day no-cost pilots with mining operators this quarter — DARA ingests your SCADA / OPC UA tags and turns them into a live operating terminal: tonnes-per-shift forecasts, hedge effectiveness, ESG snapshots, all grounded in YOUR live plant data.

Three operators have already signed on. Want me to send the 1-page pilot spec?

— [Your name]
DARA — daraos.ai/industries/mining`,
      },
      retail_followup: {
        name: "Retail — Demo follow-up",
        industry: "Retail",
        subject: "Re: DARA demo — quick recap for {{company}}",
        body_text:
`Hey {{first_name}},

Thanks for the time today. Quick recap of what we covered for {{company}}:

→ Live POS feed from your Square (30-day backfill on connect)
→ DARA Briefing that writes your close-of-day narrative in 6 seconds
→ Hourly transaction chart + top SKUs + payment-method mix
→ $499/mo · cancel anytime · trial first

Two next steps:

1. Try the 14-day free trial: daraos.ai/signup
2. Or send me your Square sandbox creds and I'll get the demo workspace populated with your actual data tonight

Worth doing?

— [Your name]`,
      },
    };
    setEditing(seeds[kind]);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-outbound-page">
      <header className="border-b border-zinc-900 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/app/today" className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-[#FFB000]" />
              <span className="font-heading font-black tracking-tight text-lg">DARA</span>
            </Link>
            <div className="hidden sm:block w-px h-6 bg-zinc-800" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// ADMIN · OUTBOUND</div>
              <h1 className="font-heading font-black text-lg tracking-tight">Email Campaigns</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => seedTemplate("hospitality_intro")} data-testid="outbound-seed-hosp"
              className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
              + HOSPITALITY
            </button>
            <button onClick={() => seedTemplate("mining_pilot")} data-testid="outbound-seed-mining"
              className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
              + MINING
            </button>
            <button onClick={() => seedTemplate("retail_followup")} data-testid="outbound-seed-retail"
              className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
              + RETAIL
            </button>
            <button onClick={() => setEditing({})} data-testid="outbound-add-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> BLANK TEMPLATE
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {loading && <div className="text-center py-20 font-mono text-[11px] uppercase tracking-widest text-zinc-600">LOADING…</div>}

        {!loading && templates.length === 0 && (
          <div className="border border-zinc-900 bg-zinc-950/40 p-10 text-center">
            <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg">No outbound templates yet</h3>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed">
              Click <strong className="text-white">+ HOSPITALITY</strong> / <strong className="text-white">+ MINING</strong> / <strong className="text-white">+ RETAIL</strong> for pre-written cold-outreach templates,
              or <strong className="text-white">BLANK TEMPLATE</strong> to start from scratch.
              Use <code className="text-[#FFB000]">&#123;&#123;first_name&#125;&#125;</code> / <code className="text-[#FFB000]">&#123;&#123;company&#125;&#125;</code> merge tags.
            </p>
          </div>
        )}

        {!loading && templates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="outbound-template-grid">
            {templates.map(t => (
              <TemplateCard
                key={t.id} t={t}
                onEdit={() => setEditing(t)}
                onSend={() => setSender({ template: t, prospect_ids: [] })}
                onDelete={() => remove(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <TemplateEditor t={editing} setT={setEditing} onSave={save} onClose={() => setEditing(null)} busy={busy} />
      )}

      {sender && (
        <SenderDialog sender={sender} setSender={setSender} prospects={prospects} onSend={sendBatch} onClose={() => setSender(null)} busy={busy} />
      )}
    </div>
  );
}

function TemplateCard({ t, onEdit, onSend, onDelete }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-4 flex flex-col" data-testid={`outbound-card-${t.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-heading font-black text-sm text-white truncate flex-1 min-w-0">{t.name}</div>
        {t.industry && <span className="px-1.5 py-0.5 border border-zinc-800 bg-black font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex-shrink-0">{t.industry}</span>}
      </div>
      <div className="font-mono text-[10px] text-zinc-500 mt-1 truncate" title={t.subject}>{t.subject}</div>
      <p className="text-xs text-zinc-400 mt-2 line-clamp-3 flex-1 leading-relaxed font-mono whitespace-pre-line">{t.body_text}</p>
      <div className="font-mono text-[9px] text-zinc-700 mt-3 pt-3 border-t border-zinc-900">
        Sent <span className="text-zinc-400">{t.times_sent || 0}</span> times
        {t.last_sent_at && <> · last <span className="text-zinc-400">{new Date(t.last_sent_at).toLocaleDateString()}</span></>}
      </div>
      <div className="flex items-center gap-1 mt-3">
        <button onClick={onSend} data-testid={`outbound-send-${t.id}`}
          className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 inline-flex items-center gap-1 flex-1 justify-center">
          <Send className="w-3 h-3" /> SEND
        </button>
        <button onClick={onEdit} data-testid={`outbound-edit-${t.id}`}
          className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1">
          <Edit3 className="w-3 h-3" />
        </button>
        <button onClick={onDelete} data-testid={`outbound-delete-${t.id}`}
          className="border border-[#FF6B47]/30 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-2 inline-flex items-center justify-center">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function TemplateEditor({ t, setT, onSave, onClose, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl bg-black border-l border-zinc-800 overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between sticky top-0 bg-black/95 backdrop-blur z-10">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">// {t.id ? "EDIT" : "NEW"} TEMPLATE</div>
            <div className="font-heading font-black text-base mt-0.5">{t.name || "Untitled"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Input label="TEMPLATE NAME *" value={t.name} onChange={v => setT({ ...t, name: v })} placeholder="Hospitality — Cold intro" testid="ot-name" />
          <Input label="INDUSTRY TAG" value={t.industry} onChange={v => setT({ ...t, industry: v })} placeholder="Hospitality · Mining · Retail" testid="ot-industry" />
          <Input label="SUBJECT *" value={t.subject} onChange={v => setT({ ...t, subject: v })} placeholder="Quick one — {{company}}..." testid="ot-subject" />
          <div className="border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">MERGE TAGS — click to copy</div>
            <div className="flex flex-wrap gap-2">
              {MERGE_TAGS.map(tag => (
                <button key={tag} onClick={() => { navigator.clipboard.writeText(tag); toast.success(`Copied ${tag}`); }}
                  className="px-2 py-1 border border-zinc-800 hover:border-[#FFB000] bg-black font-mono text-[10px] text-[#FFB000] inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> {tag}
                </button>
              ))}
            </div>
          </div>
          <Textarea label="BODY (TEXT) *" rows={12} value={t.body_text} onChange={v => setT({ ...t, body_text: v })}
            placeholder={`Hi {{first_name}},\n\nQuick one for {{company}}...`} testid="ot-body-text" />
          <Textarea label="BODY (HTML — OPTIONAL)" rows={6} value={t.body_html} onChange={v => setT({ ...t, body_html: v })}
            placeholder="HTML version. Leave blank to send text-only." testid="ot-body-html" />
        </div>
        <div className="border-t border-zinc-900 p-4 flex justify-end gap-2 sticky bottom-0 bg-black">
          <button onClick={onClose} disabled={busy} className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">CANCEL</button>
          <button onClick={onSave} disabled={busy} data-testid="ot-save-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="w-3 h-3" /> {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SenderDialog({ sender, setSender, prospects, onSend, onClose, busy }) {
  const [filter, setFilter] = useState("");
  const filtered = prospects.filter(p =>
    p.email && (
      !filter ||
      (p.company || "").toLowerCase().includes(filter.toLowerCase()) ||
      (p.email || "").toLowerCase().includes(filter.toLowerCase()) ||
      (p.source || "").toLowerCase().includes(filter.toLowerCase()) ||
      (p.stage || "").toLowerCase().includes(filter.toLowerCase())
    )
  );
  const togglePid = (id) => {
    setSender(s => ({
      ...s,
      prospect_ids: s.prospect_ids.includes(id) ? s.prospect_ids.filter(x => x !== id) : [...s.prospect_ids, id],
      preview: undefined,
    }));
  };
  const selectAll = () => setSender(s => ({ ...s, prospect_ids: filtered.slice(0, 50).map(p => p.id), preview: undefined }));
  const clearAll = () => setSender(s => ({ ...s, prospect_ids: [], preview: undefined }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] bg-black border border-zinc-800 overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between sticky top-0 bg-black/95 backdrop-blur z-10">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">// SEND CAMPAIGN</div>
            <div className="font-heading font-black text-base mt-0.5">{sender.template.name}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <input
              type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter by company, email, source, stage"
              data-testid="sender-filter"
              className="flex-1 min-w-[200px] bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
            />
            <button onClick={selectAll} data-testid="sender-select-all" className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
              SELECT ALL ({Math.min(filtered.length, 50)})
            </button>
            <button onClick={clearAll} className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300">CLEAR</button>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
              {sender.prospect_ids.length} / 50 selected
            </div>
          </div>

          {/* Prospect list */}
          <div className="border border-zinc-900 max-h-[300px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="text-center py-8 font-mono text-[10px] text-zinc-600">No prospects match the filter (must have email).</div>
            )}
            {filtered.map(p => {
              const checked = sender.prospect_ids.includes(p.id);
              const disabled = !checked && sender.prospect_ids.length >= 50;
              return (
                <label key={p.id} className={`flex items-center gap-3 px-3 py-2 border-b border-zinc-900 cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-zinc-950"} ${checked ? "bg-zinc-950" : ""}`}>
                  <input type="checkbox" checked={checked} disabled={disabled}
                    onChange={() => togglePid(p.id)} data-testid={`sender-pid-${p.id}`}
                    className="accent-[#FFB000] w-4 h-4" />
                  <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3 font-heading font-bold text-xs text-white truncate">{p.company || "—"}</div>
                    <div className="col-span-3 font-mono text-[10px] text-zinc-400 truncate">{p.email}</div>
                    <div className="col-span-2 font-mono text-[9px] uppercase tracking-widest text-zinc-600 truncate">{(p.source || "").replace("_", " ")}</div>
                    <div className="col-span-2 font-mono text-[9px] uppercase tracking-widest text-zinc-600">{p.stage.replace("_", " ")}</div>
                    <div className="col-span-2 font-mono text-[9px] text-zinc-700 text-right truncate">{p.name || ""}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Preview from dry run */}
          {sender.preview && sender.preview.length > 0 && (
            <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.02] mt-4 p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-3">// RENDERED PREVIEW (first 3)</div>
              {sender.preview.slice(0, 3).map((p, i) => (
                <div key={i} className="border-t border-zinc-900 first:border-t-0 py-2">
                  <div className="font-mono text-[10px] text-zinc-500">→ {p.company}: <span className="text-zinc-300">{p.rendered_subject}</span></div>
                  <div className="font-mono text-[10px] text-zinc-400 mt-1 line-clamp-2">{p.rendered_text_preview}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-zinc-900 p-4 flex justify-between gap-2 sticky bottom-0 bg-black">
          <button onClick={() => onSend(true)} disabled={busy || sender.prospect_ids.length === 0} data-testid="sender-dry-run"
            className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2 disabled:opacity-30">
            <Eye className="w-3 h-3" /> DRY RUN
          </button>
          <button onClick={() => onSend(false)} disabled={busy || sender.prospect_ids.length === 0} data-testid="sender-send-real"
            className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-30">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3 h-3" />}
            {busy ? "SENDING…" : `SEND ${sender.prospect_ids.length} EMAILS`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]" />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000] resize-none" />
    </label>
  );
}
